import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { pageKeyForPath } from './helpContent';

/**
 * Persistent "?" in the header. Opens plain-language help for whatever page
 * you're on. Content is i18n data, so it works with no network and no AI.
 */
export default function HelpButton() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const pageKey = pageKeyForPath(location.pathname);
  // Fall back to the generic blurb rather than showing a raw key if a page
  // has no authored content yet.
  const title = t(`help.${pageKey}.title`, t('help.default.title'));
  const body = t(`help.${pageKey}.body`, t('help.default.body'));

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-[48px] w-[48px] sm:h-10 sm:w-10 flex-shrink-0"
        onClick={() => setOpen(true)}
        data-testid="help-button"
        tooltip={t('help.buttonLabel', 'Help')}
        aria-label={t('help.buttonLabel', 'Help')}
      >
        <HelpCircle className="h-5 w-5" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-sm p-0 flex flex-col [&>button]:hidden"
          data-testid="help-sheet"
        >
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle
                className="text-lg font-bold"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {t('help.sheetTitle', 'Help')}
              </SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 md:h-8 md:w-8"
                onClick={() => setOpen(false)}
                aria-label={t('common.close', 'Close')}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-base font-semibold mb-2" data-testid="help-title">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{body}</p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
